/* istanbul ignore file */
import { normalizeError } from 'core/common/errors';
import { CookiesService } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { utmTagsCookieMaxAgeSeconds } from 'core/user/constants';
import { POSSIBLE_UTM_TAGS, UtmSource, UtmTags } from 'core/user/entities';
import { UtmTagsService, UtmTagsStorage } from './UtmTagsService';

export class UtmTagsServerService implements UtmTagsService {
  private readonly cookiesService: CookiesService;

  private readonly logger: Logger;

  private readonly storageKey = 'nebula_utm_tags';

  private readonly queryParams: Record<string, string>;

  private constructor(
    cookiesService: CookiesService,
    logger: Logger,
    queryParams: Record<string, string> = {},
  ) {
    this.cookiesService = cookiesService;
    this.logger = logger;
    this.queryParams = queryParams;
  }

  getUtmTags({ storage = 'cookie' }: { storage?: UtmTagsStorage } = {}) {
    return storage === 'session' ? this.getTagsFromQueryParams() : this.getTagsFromCache();
  }

  getQueryUtmTags(): UtmTags {
    return this.getTagsFromQueryParams();
  }

  getUtmSource({ storage = 'cookie' }: { storage?: UtmTagsStorage } = {}): UtmSource {
    if (storage === 'session') {
      return this.getUtmTags({ storage }).utm_source ?? UtmSource.NO_SOURCE;
    }

    return (
      this.getUtmTags().utm_source ?? (this.getUtmTagFromQueryParams('utm_source') as UtmSource)
    );
  }

  getUtmCookies(): string {
    return this.getUtmTagFromQueryParams('utm_cookies') ?? this.getUtmTags().utm_cookies;
  }

  getUtmContent(): string {
    return this.getUtmTagFromQueryParams('utm_content') ?? this.getUtmTags().utm_content;
  }

  getUtmAdName(): string {
    return this.getUtmTags().utm_ad_name ?? this.getUtmTagFromQueryParams('utm_ad_name');
  }

  static createFromQueryParams(
    cookiesService: CookiesService,
    logger: Logger,
    queryParams?: Record<string, string>,
  ) {
    const utmService = new UtmTagsServerService(cookiesService, logger, queryParams);

    utmService.loadFormQueryParams();

    return utmService;
  }

  updateUtmTags(utmTags: UtmTags) {
    const oldUtmTags = this.getUtmTags();
    const newUtmTags = { ...oldUtmTags, ...utmTags };

    this.saveToCache(newUtmTags);

    return newUtmTags;
  }

  getTagsFromCache() {
    try {
      const rawUtmTags = this.cookiesService.get(this.storageKey);
      const utmTags = rawUtmTags ? JSON.parse(rawUtmTags) : {};

      return this.excludeUtmTags(utmTags, ['quiz']);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
      return {};
    }
  }

  private loadFormQueryParams() {
    this.updateUtmTags(this.getTagsFromQueryParams());
  }

  private getTagsFromQueryParams(): UtmTags {
    const newUtmTags: Record<string, string> = {};

    POSSIBLE_UTM_TAGS.forEach((utmTag) => {
      if (utmTag in this.queryParams) {
        newUtmTags[utmTag] = this.extractUtmTagFromParsedQs(this.queryParams[utmTag]);
      }
    });

    return newUtmTags;
  }

  private extractUtmTagFromParsedQs(parsedQs: undefined | string | string[]): string {
    if (!parsedQs) return '';

    return Array.isArray(parsedQs) ? parsedQs[0] : parsedQs;
  }

  private excludeUtmTags(utmTags: UtmTags, excludeList: Array<string>): UtmTags {
    const newUtmTags: Record<string, string> = {};

    Object.entries(utmTags).forEach(([utmTag, utmValue]) => {
      if (excludeList.includes(utmTag)) return;

      newUtmTags[utmTag] = utmValue;
    });

    return newUtmTags;
  }

  private saveToCache(utmTags: UtmTags) {
    try {
      const stringifiedTags = JSON.stringify(utmTags);

      this.cookiesService.set(this.storageKey, stringifiedTags, {
        maxAge: utmTagsCookieMaxAgeSeconds,
        path: '/',
      });
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);
    }
  }

  private getUtmTagFromQueryParams(tag: keyof UtmTags): string {
    return this.queryParams[tag] ?? '';
  }
}
